import { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme, urlScheme } from 'src/utils/validation-schemes';

import type { ProjectLink } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';

export const ProjectLinkPopup = ({
  link = null,
  disabled = false,
  addProjectLink = () => {},
  editProjectLink = () => {},
}: {
  link?: ProjectLink | null;
  disabled?: boolean;
  addProjectLink?: (link: ProjectLink) => void;
  editProjectLink?: (link: ProjectLink) => void;
}) => {
  const [open, setOpen] = useState(false);
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      pf_page_name: link ? link.pf_page_name : '',
      pf_page_description: link ? link.pf_page_description : '',
      pf_page_url: link ? link.pf_page_url : '',
    },
    resolver: yupResolver(
      object({
        pf_page_name: stringScheme({ required: true }).label('Page name*'),
        pf_page_description: stringScheme({ required: true }).label('Description*'),
        pf_page_url: urlScheme({ required: true }).label('Link*'),
      }),
    ),
  });

  const submit = (data: Required<ProjectLink>) => {
    link ? editProjectLink(data) : addProjectLink(data);
    close();
  };

  const close = () => {
    reset();
    setOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setOpen(true);
    if (!open) {
      close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {link ? (
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              className={'opacity-30 hover:opacity-80'}
            >
              <path
                d="M4.55556 14.933H5.65533L12.8996 7.68873L11.7998 6.58895L4.55556 13.8332V14.933ZM17 16.4885H3V13.1884L13.4494 2.73895C13.5953 2.59314 13.7931 2.51123 13.9993 2.51123C14.2056 2.51123 14.4034 2.59314 14.5492 2.73895L16.7496 4.93929C16.8954 5.08514 16.9773 5.28294 16.9773 5.48918C16.9773 5.69541 16.8954 5.89321 16.7496 6.03906L7.85567 14.933H17V16.4885ZM12.8996 5.48918L13.9993 6.58895L15.0991 5.48918L13.9993 4.3894L12.8996 5.48918Z"
                fill="#05050D"
              />
            </svg>
          </div>
        ) : (
          <div className={'text-[#7A6BE5] text-xs font-medium cursor-pointer hover:underline'}>
            Add website link +
          </div>
        )}
      </DialogTrigger>
      <DialogContent closeButtonHidden>
        <DialogHeader>
          <DialogTitle>Add website link</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="content">
            <form
              id="add-project-link"
              onSubmit={(e) => {
                e.stopPropagation();

                return handleSubmit(submit)(e);
              }}
            >
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="pf_page_name"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'Page name*'}
                      placeholder={'CoSupport AI'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="pf_page_description"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'Description*'}
                      placeholder={'CoSupport AI is an all-in-one AI solution for customer support'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="pf_page_url"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'Link*'}
                      placeholder={'https://cosupport.ai'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div className="flex justify-center mt-5">
                <div className="flex gap-2.5">
                  <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    form={'add-project-link'}
                    disabled={!isDirty || !isValid}
                    className="min-w-24"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
